import React, { useState, useEffect } from 'react'
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import './assets/global.css'
import Home from './pages/home/home.jsx'
import Navbar from './components/Navbar'
import Sidebar from './components/Sidebar'
import LoginPage from './components/login'
import Details from './pages/home/details.jsx'
import StudentRegistration from './pages/students/studentRegistration/StudentRegistration.jsx'
import GenerateCertificate from './pages/generateCertificate/page.jsx'
import Attendance from './pages/attendance/attendance.jsx'
import Batch from './pages/batch/page.jsx'
import BatchDetails from './pages/batch/batchDetails.jsx'
import Expanse from './pages/finances/expense/page.jsx'
import { getCookie } from './app/axios/CookieConfig'
import * as api from './app/axios/api.jsx'
import { Toaster } from 'sonner'
import Fee from './pages/finances/fee/fee.jsx'
import Record from './pages/record/page.jsx'
import Students from './pages/students/students/page.jsx'
import { RoleProvider } from './context/RoleContext.js'
import EmployeeForm from './pages/employees/employee/page.jsx'
import EmployeeList from './pages/employees/employeeList/page.jsx'
import Finance from './pages/finances/finance/page.jsx'
import FeeHandoverSystem from './pages/finances/handvoer/page.jsx'
import employeeDetails from './pages/employees/employee/employeeDetails.jsx'
import Collection from './pages/finances/Collection/page.jsx'
import LaptopManage from './pages/laptopManage/page.jsx'
import Investment from './pages/finances/Investment/page.jsx'
import FinancialDashboard from './pages/finances/dashboard/page.jsx'
import summerCourse from './pages/summerDetails/summerCourses/page.jsx'
import SummerDashboard from './pages/summerDetails/summerDashboard/page.jsx'
import SummerRegistrationDetails from './pages/summerDetails/summerDashboard/details.jsx'

// Admin Layout with Sidebar and Navbar
const AdminLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    if (saved !== null) {
      return JSON.parse(saved)
    }
    return window.innerWidth < 1024
  })

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed))
  }, [sidebarCollapsed])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  return (
    <div className='min-h-screen bg-gray-50'>
      <Sidebar
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
      />
      <Navbar
        toggleSidebar={toggleSidebar}
        sidebarCollapsed={sidebarCollapsed}
      />
      <div
        className={`transition-all duration-300 ${
          sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'
        }`}
      >
        <main className='pt-20 lg:pt-16'>
          <div className='p-4 sm:p-6'>{children}</div>
        </main>
      </div>
    </div>
  )
}

// Protected Route Component
const ProtectedRoute = ({ isLogin, children }) => {
  return isLogin ? children : <Navigate to='/login' />
}

function AppRoutes({ isLogin, setIsLogin }) {
  const checkLogin = () => {
    const token = getCookie('token')
    if (!token) {
      setIsLogin(false)
    } else {
      api.setAuthHeader('token')
    }
  }

  // useEffect(() => {
  //   const loginInterval = setInterval(checkLogin, 1000)
  //   return () => clearInterval(loginInterval)
  // }, [isLogin])
  useEffect(() => {
    checkLogin()
  }, [checkLogin])

  // *** FIX: Moved helper functions here, before the return statement ***

  // Helper function to wrap component in the layout and protected route
  const wrapInLayout = (Component, props = {}) => (
    <ProtectedRoute isLogin={isLogin}>
      <AdminLayout>
        <Component {...props} />
      </AdminLayout>
    </ProtectedRoute>
  )

  // Placeholder component for unimplemented pages
  const Placeholder = ({ title, description }) => (
    <div className='p-6 bg-white rounded-xl shadow-sm'>
      <h1 className='text-2xl font-bold text-gray-800 mb-4'>{title}</h1>
      <p className='text-gray-600'>{description}</p>
    </div>
  )

  return (
    <Routes>
      {/* Login Route - No Layout */}
      <Route path='/login' element={<LoginPage />} />
      {/* Protected Routes using the helper function */}
      <Route path='/' element={wrapInLayout(Home)} />
      <Route path='/students/:id' element={wrapInLayout(Details)} />
      <Route path='/expanse' element={wrapInLayout(Expanse)} />
      <Route
        path='/studentRegistration'
        element={wrapInLayout(StudentRegistration)}
      />
      <Route
        path='/generateCertificate'
        element={wrapInLayout(GenerateCertificate)}
      />
      <Route path='/attendance' element={wrapInLayout(Attendance)} />
      <Route path='/batch' element={wrapInLayout(Batch)} />
      <Route path='/batchDetails/:id' element={wrapInLayout(BatchDetails)} />

      <Route path='/finance' element={wrapInLayout(Finance)} />
      <Route
        path='/financeDashboard'
        element={wrapInLayout(FinancialDashboard)}
      />

      <Route
        path='/dailyCollection'
        element={wrapInLayout(FeeHandoverSystem)}
      />
      <Route path='/collection' element={wrapInLayout(Collection)} />
      <Route path='/investment' element={wrapInLayout(Investment)} />

      <Route path='/record' element={wrapInLayout(Record)} />
      <Route path='/employee' element={wrapInLayout(EmployeeForm)} />
      <Route path='/employees' element={wrapInLayout(EmployeeList)} />
      <Route path='/employees/:id' element={wrapInLayout(employeeDetails)} />

      {/* Additional Routes for Sidebar Menu Items */}
      <Route path='/students' element={wrapInLayout(Students)} />
      <Route path='/lapTopManage' element={wrapInLayout(LaptopManage)} />
      <Route path='/summerCourse' element={wrapInLayout(summerCourse)} />
      <Route path='/summerDashboard' element={wrapInLayout(SummerDashboard)} />
      <Route
        path='/summerRegistration/:id'
        element={wrapInLayout(SummerRegistrationDetails)}
      />

      <Route
        path='/reports/attendance'
        element={wrapInLayout(Placeholder, {
          title: 'Attendance Report',
          description:
            'Attendance analytics page - implement your report component here',
        })}
      />

      <Route path='/fee' element={wrapInLayout(Fee)} />

      <Route
        path='/settings'
        element={wrapInLayout(Placeholder, {
          title: 'Settings',
          description:
            'Application settings page - implement your settings component here',
        })}
      />
      {/* Catch all route - redirect to home */}
      <Route path='*' element={<Navigate to='/' />} />
    </Routes>
  )
}

export default function App() {
  const [role, setRole] = useState(() => {
    const userRole = getCookie('role')
    return userRole ? userRole : null
  })
  const [profileEmail, setProfileEmail] = useState(null)
  const [profileName, setProfileName] = useState(null)
  const [profileId, setProfileId] = useState(null)
  const [isLogin, setIsLogin] = useState(() => {
    const token = getCookie('token')
    if (token) {
      api.setAuthHeader('token')
      return true
    }
    return false
  })

  return (
    <BrowserRouter>
      <AuthProvider value={{ isLogin, setIsLogin }}>
        <RoleProvider
          value={{
            role,
            setRole,
            profileEmail,
            setProfileEmail,
            profileName,
            setProfileName,
            profileId,
            setProfileId,
          }}
        >
          <AppRoutes isLogin={isLogin} setIsLogin={setIsLogin} />
        </RoleProvider>
      </AuthProvider>

      <Toaster
        position='top-center'
        richColors
        closeButton
        expand={true}
        visibleToasts={5}
      />
    </BrowserRouter>
  )
}
